import { useEffect, useRef } from 'react';

import { mParticle } from '@local/analytics';

import {
  useCookieConsentEvent,
  CONSENT_GROUP,
} from '@local/third-party-offers';

import { ANALYTICS_EVENT_NAME } from 'helpers/constants';
import { ANALYTICS_EVENT_TYPES, logAnalyticsEvent } from 'helpers/loggers';

type MParticleConsentState = ReturnType<
  typeof mParticle.Consent.createConsentState
>;

type UserWithSetConsentState = {
  setConsentState: (state: MParticleConsentState) => void;
};

// Not in every browser we support, nor in every TS lib version.
type NavigatorWithUserActivation = Navigator & {
  userActivation?: { hasBeenActive: boolean };
};

const USER_GESTURE_EVENTS = ['pointerdown', 'keydown'] as const;

// Normalize so a reordered re-serialization of the same consent compares equal.
const normalizeGroups = (activeCookieGroups: string): string =>
  activeCookieGroups.split(',').filter(Boolean).sort().join(',');

const buildConsentAttributes = (activeCookieGroups: string) => ({
  functional_cookies_accepted: activeCookieGroups.includes(
    CONSENT_GROUP.FUNCTIONAL
  ),
  performance_cookies_accepted: activeCookieGroups.includes(
    CONSENT_GROUP.PERFORMANCE
  ),
  social_cookies_accepted: activeCookieGroups.includes(CONSENT_GROUP.SOCIAL),
  targeting_cookies_accepted: activeCookieGroups.includes(
    CONSENT_GROUP.TARGETING
  ),
});

/**
 * App-level bridge between OneTrust and mParticle. Keeps the one-trust package
 * vendor-agnostic: it observes consent, this hook owns the mParticle translation
 * and the `select_cookie_consent` analytics event (fired only on user-driven
 * changes, not the initial stored-consent sync).
 */
export const useCookieConsentSync = (): void => {
  const lastConsentRef = useRef<string | null>(null);
  const sawUserGestureRef = useRef(false);

  // Necessary, not sufficient: any gesture passes, but a consent change the user
  // made requires one, so no gesture at all means no user change — which is the
  // loop, since OneTrust can re-derive a different value on every load
  // (CN-50375). Worst case is one reload on a page someone touched, not forever.
  useEffect(() => {
    const markGesture = (): void => {
      sawUserGestureRef.current = true;
    };

    const options = { capture: true, once: true };
    USER_GESTURE_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, markGesture, options);
    });

    return () => {
      USER_GESTURE_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, markGesture, options);
      });
    };
  }, []);

  const hasUserActedOnThisDocument = (): boolean =>
    sawUserGestureRef.current ||
    (window.navigator as NavigatorWithUserActivation).userActivation
      ?.hasBeenActive === true;

  useCookieConsentEvent({
    onConsentChange: ({
      activeCookieGroups,
      isGpcSignalPresent,
      isInitial,
    }) => {
      const normalizedGroups = normalizeGroups(activeCookieGroups);
      const hasConsentChanged = normalizedGroups !== lastConsentRef.current;
      lastConsentRef.current = normalizedGroups;

      if (!isInitial) {
        logAnalyticsEvent({
          eventName: ANALYTICS_EVENT_NAME.selectCookieConsent,
          eventType: ANALYTICS_EVENT_TYPES.UserPreference,
          attributes: buildConsentAttributes(activeCookieGroups),
        });
      }

      // `ec557f3f2` globally opts mParticle out when Performance is withheld;
      // respect that same behavior here.
      mParticle.setOptOut(
        !activeCookieGroups.includes(CONSENT_GROUP.PERFORMANCE)
      );

      const isOptedOut =
        !activeCookieGroups.includes(CONSENT_GROUP.TARGETING) ||
        isGpcSignalPresent;

      mParticle.ready(() => {
        const user = mParticle.Identity?.getCurrentUser?.();
        if (!user) {
          return;
        }

        const ccpaConsent = mParticle.Consent.createCCPAConsent(isOptedOut);
        if (!ccpaConsent) {
          return;
        }

        const state = mParticle.Consent.createConsentState();
        state.setCCPAConsentState(ccpaConsent);

        const userWithConsent = user as unknown as UserWithSetConsentState;
        userWithConsent.setConsentState(state);
      });

      // mparticle + rokt doesn't do lazy loading nor unloading, reload to respect user choices.
      // Skip when embedded: consent cookies are third-party there and can fail to persist
      // before the reload, re-showing the banner (CN-50312).
      // Skip when unchanged: OneTrust can re-fire with the same selection, looping the reload (CN-50375).
      // Skip with no user gesture: nobody changed anything (CN-50375).
      const isEmbedded = window.top !== window;
      if (
        !isInitial &&
        !isEmbedded &&
        hasConsentChanged &&
        hasUserActedOnThisDocument()
      ) {
        mParticle.upload();
        window.location.reload();
      }
    },
  });
};
